import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Pin } from 'lucide-react';
import BBALoader from '../BBALoader';
import { GlassCard } from '../glass/GlassUi';
import { REPORT_GROUPS } from '../../utils/reportCatalog';
import { REPORT_PAGE_COMPONENTS } from '../../pages/reportPages';
import { ReportCardsHost, parsePinCardKey } from './reportCards';

const SLOW_CARD_MS = 20000;
const NO_SLOTS = {};

// Shown in a slot until its card portals in; after a while it offers the report and an unpin
const CardPlaceholder = ({ report, onUnpin }) => {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), SLOW_CARD_MS);
    return () => clearTimeout(timer);
  }, []);
  return (
    <GlassCard className="flex h-full min-h-[10rem] flex-col items-center justify-center gap-3 p-5 text-center">
      {slow ? (
        <>
          <p className="text-sm text-gray-500 dark:text-white/[0.45]">This card isn’t showing here right now.</p>
          <div className="flex items-center gap-3 text-xs font-medium">
            <Link to={report.path} className="text-[#F80707] hover:underline dark:text-[#FF4D40]">Open {report.title}</Link>
            <button type="button" onClick={onUnpin} className="text-gray-500 hover:text-[#F80707] dark:text-white/50">Unpin</button>
          </div>
        </>
      ) : (
        <BBALoader size={56} />
      )}
    </GlassCard>
  );
};

/**
 * /crm/reports My view: every card the staff member pinned, grouped by report, live.
 * Each report with pins renders off-screen once (ReportCardsHost) and its pinned
 * cards portal into the slots below.
 */
const PinnedReportCards = ({ pinKeys, isVisible, togglePin }) => {
  const [slots, setSlots] = useState({}); // reportId -> { cardId: element }
  const [mounted, setMounted] = useState({}); // pin key -> size

  // Pins grouped by report in catalog order, cards in the order they were pinned
  const sections = useMemo(() => {
    const byReport = new Map();
    pinKeys.forEach((key) => {
      const parsed = parsePinCardKey(key);
      if (!parsed) return;
      if (!byReport.has(parsed.reportId)) byReport.set(parsed.reportId, []);
      byReport.get(parsed.reportId).push({ key, cardId: parsed.cardId });
    });
    return REPORT_GROUPS.flatMap((group) => group.reports)
      .filter((report) => byReport.has(report.id) && REPORT_PAGE_COMPONENTS[report.id] && isVisible(report))
      .map((report) => ({ report, cards: byReport.get(report.id) }));
  }, [pinKeys, isVisible]);

  // Stable ref callback per slot so React doesn't detach / re-attach on every render
  const refCallbacks = useRef({});
  const slotRef = (reportId, cardId) => {
    const key = `${reportId}/${cardId}`;
    if (!refCallbacks.current[key]) {
      refCallbacks.current[key] = (el) => setSlots((prev) => {
        const current = prev[reportId] || NO_SLOTS;
        if ((current[cardId] || null) === el) return prev;
        const next = { ...current };
        if (el) next[cardId] = el; else delete next[cardId];
        return { ...prev, [reportId]: next };
      });
    }
    return refCallbacks.current[key];
  };

  const hostCallbacks = useRef({});
  const callbacksFor = useCallback((reportId) => {
    if (!hostCallbacks.current[reportId]) {
      hostCallbacks.current[reportId] = {
        onMount: (cardId, size) => setMounted((prev) => ({ ...prev, [`${reportId}/${cardId}`]: size || 'half' })),
        onUnmount: (cardId) => setMounted((prev) => {
          const next = { ...prev };
          delete next[`${reportId}/${cardId}`];
          return next;
        }),
      };
    }
    return hostCallbacks.current[reportId];
  }, []);

  if (sections.length === 0) {
    return (
      <GlassCard className="px-6 py-16 text-center">
        <Pin className="mx-auto mb-3 h-8 w-8 text-[#F80707]/70" strokeWidth={1.75} />
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">No pinned cards yet</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-white/[0.45]">Open any report and tap the pin on a card to add it here.</p>
      </GlassCard>
    );
  }

  return (
    <div className="space-y-10">
      {sections.map(({ report, cards }, sectionIndex) => {
        const { onMount, onUnmount } = callbacksFor(report.id);
        return (
          <section key={report.id}>
            <ReportCardsHost
              reportId={report.id}
              Component={REPORT_PAGE_COMPONENTS[report.id]}
              slots={slots[report.id] || NO_SLOTS}
              onMount={onMount}
              onUnmount={onUnmount}
            />

            {/* Same left-aligned label + fading separator as the grid view, with a link to the report */}
            <div className="glass-rise mb-5 flex items-center gap-5" style={{ animationDelay: `${sectionIndex * 60}ms` }}>
              <h2 className="shrink-0 text-2xl font-semibold tracking-tight text-[#1C1916] dark:text-white">{report.title}</h2>
              <span className="h-px flex-1 bg-gradient-to-r from-[#F80707]/40 via-[#1C1916]/15 to-transparent dark:from-[#F80707]/50 dark:via-white/10" />
              <Link
                to={report.path}
                className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-gray-400 transition-colors hover:text-[#F80707] dark:text-white/40 dark:hover:text-[#FF4D40]"
              >
                Open report
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {cards.map(({ key, cardId }) => {
                const size = mounted[key];
                return (
                  <div key={key} className={`min-w-0 ${size === 'wide' ? 'lg:col-span-2' : ''}`}>
                    {!size && <CardPlaceholder report={report} onUnpin={() => togglePin(key)} />}
                    <div ref={slotRef(report.id, cardId)} className="h-full [&>*]:h-full" />
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default PinnedReportCards;
